import { useMemo, useRef } from "react";

import { ApiError } from "../errors/ApiError";

export type IdempotencyKey = {
  // Ключ действия `scope` (например, `match:42`). Пока действие не завершено, ключ тот же:
  // и в повторах TanStack Query (ключ лежит в переменных мутации), и при повторном нажатии
  // после обрыва сети. Другой `scope` — другое действие и новый ключ.
  keyFor: (scope: string) => string;
  // Вызывается в `onSettled` мутации. Освобождает ключ, если сервер дал окончательный ответ:
  // успех или ошибка 4xx. После сетевой ошибки или 5xx ключ остаётся — неизвестно,
  // создал ли сервер что-нибудь, и повтор должен вернуть уже созданное.
  settle: (error: unknown) => void;
};

const isFinal = (error: unknown): boolean =>
  error === null || error === undefined || (error instanceof ApiError && error.status < 500);

// Один `Idempotency-Key` на одно действие пользователя (docs/frontend.md, «Повторы и идемпотентность»).
export const useIdempotencyKey = (): IdempotencyKey => {
  const current = useRef<{ scope: string; key: string } | null>(null);
  return useMemo(
    () => ({
      keyFor: (scope) => {
        if (current.current?.scope !== scope) {
          current.current = { scope, key: crypto.randomUUID() };
        }
        return current.current.key;
      },
      settle: (error) => {
        if (isFinal(error)) {
          current.current = null;
        }
      },
    }),
    [],
  );
};
